import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import LoadingSpinner from '../loading-spinner/loading-spinner';

import { useSectionLoadingSpinner } from '../../shared/hooks/hooks';

import './section-loading-spinner.scss';

interface SectionLoadingSpinnerProps {
  children: React.ReactNode;
  isActive: boolean;
  isCriticalTimeMessage?: boolean;
  isFullPage?: boolean;
  message?: string;
  shouldShowLongLoadTimeMessage?: boolean;
  shouldSpinOnRender?: boolean;
}

function SectionLoadingSpinner(props: SectionLoadingSpinnerProps) {
  const {
    children,
    isActive,
    isCriticalTimeMessage = false,
    isFullPage = false,
    message,
    shouldShowLongLoadTimeMessage = false,
    shouldSpinOnRender = false
  } = props;
  const { setShowSpinner, showSpinner } = useSectionLoadingSpinner();
  const { pathname } = useLocation();

  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!shouldSpinOnRender) {
      setShowSpinner(false);
    }
  }, [pathname, setShowSpinner, shouldSpinOnRender]);

  function getSpinnerBoxPosition() {
    if (sectionRef.current) {
      const windowHeight = window.innerHeight;
      const headerHeight =
        document.querySelector('header')?.getBoundingClientRect().height || 0;
      const elemDimensions = sectionRef.current.getBoundingClientRect();
      const elemHeight = elemDimensions.height;
      const elemTop = elemDimensions.top;
      const elemBottom = elemDimensions.bottom;

      const isTopPositive = elemTop - headerHeight >= 0;

      if (isTopPositive) {
        if (windowHeight >= elemBottom) {
          return {
            height: '100%'
          };
        } else {
          return {
            height: windowHeight - elemTop,
            top: 0
          };
        }
      } else {
        return {
          bottom: 0,
          height: elemHeight + (elemTop - headerHeight)
        };
      }
    }

    return { height: '100%' };
  }

  return (
    <div
      data-testid="section-spinner-container"
      ref={sectionRef}
      styleName="container"
    >
      {isActive && showSpinner && (
        <div
          data-testid="spinner-container"
          style={
            isFullPage
              ? {
                  height: '100vh',
                  position: 'fixed',
                  top: 0,
                  width: '100vw',
                  zIndex: 11
                }
              : {}
          }
          styleName="spinner-container"
        >
          <div
            style={
              isFullPage ? { height: '100%' } : { ...getSpinnerBoxPosition() }
            }
            styleName="spinner-box"
          >
            <LoadingSpinner
              isCriticalTimeMessage={isCriticalTimeMessage}
              message={message}
              shouldShowLongLoadTimeMessage={shouldShowLongLoadTimeMessage}
            />
          </div>
        </div>
      )}

      {children}
    </div>
  );
}

export default SectionLoadingSpinner;
